import { createFileRoute, Link, useRouter } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useRef } from 'react'
import { z } from 'zod'
import { filesQueryOptions, pollWhileProcessing } from '#/api/files'
import type { WorkspaceFile } from '#/api/files'
import { TABLE_DENSE_ROW_PX } from '#/components/ui/table'
import { DataTable } from '#/components/data-table'
import {
  DOCUMENT_TABLE_CLASS,
  useDocumentColumns,
} from '#/components/documents/document-table'
import { PageHeader, displayTitleClass } from '#/components/page-header'
import { RoutePending } from '#/components/route-pending'
import { RouteError } from '#/components/route-error'
import { UploadDropzone, UploadLane } from '#/components/upload/upload-list'
import { useUploadsTo } from '#/components/upload/upload-queue'
import { uploadDestinationOf } from '#/components/upload/upload-queue-state'
import { useResponsivePageSize } from '#/hooks/use-responsive-page-size'
import { useCan } from '#/user/me'
import { PageContainer } from '#/components/page-container'
import { DocumentGraphDialog } from '#/components/documents/document-graph-dialog'

// `candidates` names the document whose candidate graph is open in a dialog
// over the table, so the dialog survives a reload and back closes it.
const documentsSearchSchema = z.object({
  candidates: z.string().optional(),
})

export const Route = createFileRoute(
  '/_authed/accounts/$accountId/workspaces/$workspaceId/documents/',
)({
  validateSearch: documentsSearchSchema,
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      filesQueryOptions(params.accountId, params.workspaceId),
    ),
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: WorkspaceDocuments,
})

// Approximate vertical chrome on this page: top bar (~56), page padding (64),
// heading block (~80), the one-line dropzone (44), three 24px gaps (72), table
// header row (40), and pagination row + margin (~50). Update if the dropzone's
// height or the page gap changes — there's no automatic check.
const PAGE_CHROME_PX = 406

function WorkspaceDocuments() {
  const { t } = useTranslation()
  const { accountId, workspaceId } = Route.useParams()
  const { candidates } = Route.useSearch()
  const navigate = Route.useNavigate()
  const router = useRouter()
  // While any document is still being processed the listing is read again
  // every few seconds: it carries each file's pipeline stage, which the
  // status cell draws, and the candidates link that waits on it. The upload
  // queue polls the same listing for the files in the lane, so the two share
  // one fetch.
  const { data: files } = useSuspenseQuery({
    ...filesQueryOptions(accountId, workspaceId),
    refetchInterval: pollWhileProcessing,
  })
  const { workspace } = Route.useRouteContext()
  const destination = uploadDestinationOf(t, accountId, workspace)
  const arriving = useUploadsTo(destination)
  const inLane = new Set(arriving.map((item) => item.fileUploadUuid))

  // A file on its way in shows in the lane under the dropzone; the table takes
  // it once the lane lets it go, so it is never in both.
  const tableRows = files.filter((row) => !inLane.has(row.uuid))
  const pageSize = useResponsivePageSize({
    overhead: PAGE_CHROME_PX,
    rowHeight: TABLE_DENSE_ROW_PX,
  })
  const canUpload = useCan('annwn:documents:upload')
  // The graph is the internal candidate-node graph.
  const canViewInternal = useCan('annwn:internal:read')
  // A link naming a document this workspace doesn't hold opens nothing.
  const graphFile = files.find((f) => f.uuid === candidates)
  // Set when the table's own link opened the dialog, so closing it can step
  // back to the entry before it rather than leave one Back would reopen.
  const openedFromTable = useRef(false)

  // The graph opens in a dialog over the table, by the URL, so the table's
  // page and search stay as they were underneath and a reload keeps it open.
  // Only for those who may see the internal candidate graph.
  const columns = useDocumentColumns<WorkspaceFile>({
    fileOf: (file) => file,
    graph: canViewInternal
      ? {
          link: (file, icon) => (
            <Link
              from={Route.fullPath}
              to="."
              search={(prev) => ({ ...prev, candidates: file.uuid })}
              resetScroll={false}
              onClick={() => {
                openedFromTable.current = true
              }}
              data-candidates-for={file.uuid}
            >
              {icon}
            </Link>
          ),
        }
      : undefined,
  })

  return (
    <PageContainer width="wide" className="min-h-full">
      <PageHeader title={t('documents.title')} />

      {/* A slim target above the documents; with none yet, a roomier one,
          since adding them is all the page has to offer. */}
      {canUpload ? (
        <div className="flex flex-col gap-2">
          <UploadDropzone
            destination={destination}
            compact={files.length > 0 || arriving.length > 0}
            className="bg-card"
          />
          <UploadLane destination={destination} />
        </div>
      ) : null}

      {tableRows.length === 0 && arriving.length > 0 ? null : (
        <DataTable
          data={tableRows}
          columns={columns}
          pageSize={pageSize}
          size="dense"
          initialSorting={[{ id: 'uploaded', desc: true }]}
          tableClassName={DOCUMENT_TABLE_CLASS}
          // TODO: extend search to match the uploader as well.
          searchAccessor="name"
          searchPlaceholder={t('documents.searchPlaceholder')}
          getRowId={(row) => row.uuid}
          emptyMessage={t('documents.emptyTable')}
          topRightSlot={
            <h2 className={displayTitleClass('sm')}>
              {t('documents.allDocuments')}
            </h2>
          }
        />
      )}

      <DocumentGraphDialog
        target={
          graphFile === undefined
            ? null
            : {
                accountId,
                workspaceId,
                fileUploadId: graphFile.uuid,
                fileName: graphFile.original_filename,
              }
        }
        onClose={() => {
          if (openedFromTable.current) {
            openedFromTable.current = false
            router.history.back()
            return
          }
          void navigate({
            search: (prev) => ({ ...prev, candidates: undefined }),
            resetScroll: false,
            replace: true,
          })
        }}
        // The document's own link, wherever the table has it.
        returnFocusTo={({ fileUploadId }) =>
          document.querySelector<HTMLElement>(
            `[data-candidates-for="${CSS.escape(fileUploadId)}"]`,
          )
        }
      />
    </PageContainer>
  )
}
